import * as Sentry from '@sentry/react'
import { Button } from '@viapeople/components'
import { useEffect } from 'react'
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

const isDev = import.meta.env.DEV

function describeError(error: unknown): { title: string; message: string; details?: string } {
	if (isRouteErrorResponse(error)) {
		return {
			title: error.status === 404 ? 'Page not found' : `Request failed (${error.status})`,
			message:
				typeof error.statusText === 'string' && error.statusText.length > 0
					? error.statusText
					: 'The page could not be loaded.',
			details:
				typeof error.data === 'string' ? error.data : error.data ? JSON.stringify(error.data, null, 2) : undefined,
		}
	}
	if (error instanceof Error) {
		return {
			title: 'Something went wrong',
			message: 'An unexpected error occurred while rendering this page.',
			details: `${error.name}: ${error.message}\n${error.stack ?? ''}`,
		}
	}
	return {
		title: 'Something went wrong',
		message: 'An unexpected error occurred while rendering this page.',
		details: typeof error === 'string' ? error : JSON.stringify(error, null, 2),
	}
}

export function RouteErrorBoundary() {
	const error = useRouteError()
	const navigate = useNavigate()
	const { title, message, details } = describeError(error)

	useEffect(() => {
		if (error instanceof Error) {
			Sentry.captureException(error)
		} else {
			Sentry.captureMessage(typeof error === 'string' ? error : JSON.stringify(error))
		}
		console.error('[RouteErrorBoundary]', error)
	}, [error])

	return (
		<div className='flex min-h-[60vh] items-center justify-center p-6'>
			<div className='flex w-full max-w-xl flex-col items-center gap-4 rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm'>
				<div className='flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600'>
					<svg
						width='24'
						height='24'
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2'
						aria-hidden='true'>
						<circle cx='12' cy='12' r='10' />
						<line x1='12' y1='8' x2='12' y2='12' />
						<line x1='12' y1='16' x2='12.01' y2='16' />
					</svg>
				</div>
				<h1 className='text-lg font-semibold text-neutral-900'>{title}</h1>
				<p className='text-sm text-neutral-600'>{message}</p>
				<div className='flex items-center gap-3 pt-2'>
					<Button variant='secondary' size='small' onClick={() => navigate(-1)}>
						Go back
					</Button>
					<Button variant='primary' size='small' onClick={() => window.location.reload()}>
						Reload page
					</Button>
				</div>
				{isDev && details && (
					<details className='mt-4 w-full text-left'>
						<summary className='cursor-pointer text-xs font-medium text-neutral-500 hover:text-neutral-700'>
							Error details (dev only)
						</summary>
						<pre className='mt-2 max-h-72 overflow-auto rounded-md bg-neutral-900 p-3 text-[11px] leading-snug text-neutral-100'>
							{details}
						</pre>
					</details>
				)}
			</div>
		</div>
	)
}
